"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RecordingManagementHelper = void 0;
const component_1 = require("@front/components/notifications/component");
class RecordingManagementHelper {
    /**
     * Start recording audio for the conversation
     */
    static async startRecording(component) {
        console.log('🎤 [ConversationAgent] Starting recording...', {
            currentSessionId: component.currentSessionId,
            isRecording: component.isRecording
        });
        if (!component.currentSessionId || component.isRecording) {
            console.warn('⚠️ [ConversationAgent] Cannot start recording:', {
                hasSessionId: !!component.currentSessionId,
                isAlreadyRecording: component.isRecording
            });
            return;
        }
        try {
            component.isRecording = true;
            // Clear any previous transcriptions when starting new recording
            component.currentTranscription = '';
            // Use real-time streaming instead of manual recording
            const streamingOptions = {
                sampleRate: 16000,
                channels: 1,
                bufferSize: 2048,
                chunkDuration: 100,
                enableVAD: true // Enable Voice Activity Detection by default
            };
            console.log('🚀 [ConversationAgent] Starting audio streaming with options:', streamingOptions);
            await component.conversationAgentService.startAudioStreaming(component.currentSessionId, streamingOptions);
        }
        catch (error) {
            console.error('💥 [ConversationAgent] Failed to start audio streaming:', error);
            component.isRecording = false;
            component_1.NotificationsArea.sendNotification({
                content: 'Failed to start audio streaming',
                type: 'error'
            });
        }
    }
    /**
     * Stop recording audio for the conversation
     */
    static async stopRecording(component) {
        if (!component.isRecording) {
            return;
        }
        try {
            component.isRecording = false;
            await component.conversationAgentService.stopAudioStreaming(component.currentSessionId);
            console.log('🛑 Recording stopped');
        }
        catch (error) {
            component_1.NotificationsArea.sendNotification({
                content: 'Failed to stop audio streaming',
                type: 'error'
            });
        }
    }
    /**
     * Pause recording (keeps Azure session alive for quick resume)
     */
    static async pauseRecording(component) {
        if (!component.currentSessionId || !component.isRecording) {
            console.warn('⚠️ [ConversationAgent] Cannot pause recording:', {
                hasSessionId: !!component.currentSessionId,
                isRecording: component.isRecording
            });
            return;
        }
        try {
            console.log('⏸️ [ConversationAgent] Pausing recording (keeping Azure session alive)');
            await component.conversationAgentService.pauseRecording(component.currentSessionId);
            // Keep isRecording = true in component to show mic is still active for interruptions
        }
        catch (error) {
            console.error('💥 [ConversationAgent] Failed to pause recording:', error);
        }
    }
    /**
     * Resume recording (reactivates paused Azure session)
     */
    static async resumeRecording(component) {
        if (!component.currentSessionId || !component.isRecording) {
            console.warn('⚠️ [ConversationAgent] Cannot resume recording:', {
                hasSessionId: !!component.currentSessionId,
                isRecording: component.isRecording
            });
            return;
        }
        try {
            console.log('▶️ [ConversationAgent] Resuming recording');
            await component.conversationAgentService.resumeRecording(component.currentSessionId);
        }
        catch (error) {
            console.error('💥 [ConversationAgent] Failed to resume recording:', error);
        }
    }
    /**
     * Handle user transcription (final)
     */
    static async handleUserTranscription(component, transcription) {
        console.log('📝 User transcription received:', transcription);
        console.log('🛑 [RecordingManagement] STAGE 1: User transcription complete - preparing to disable microphone');
        console.log('🎤 [RecordingManagement] MICROPHONE STATUS: About to disable for LLM processing');
        // Check for empty transcription which might indicate STT session failure
        if (!transcription || transcription.trim() === '') {
            console.warn('⚠️ [RecordingManagement] Empty transcription detected - possible STT session failure');
            console.log('🔍 [RecordingManagement] Current conversation ID:', component.currentConversationId);
            console.log('🔍 [RecordingManagement] Component state check:', {
                currentSessionId: component.currentSessionId,
                currentConversationId: component.currentConversationId,
                isConversationActive: component.isConversationActive
            });
            // Initialize recovery timer if not already started
            if (this.sttRecoveryStartTime === null) {
                this.sttRecoveryStartTime = Date.now();
                this.sttRecoveryAttempts = 0;
                console.log('🕐 [RecordingManagement] Starting FULL SESSION restart timer (2 minutes)');
            }
            // Check if we've exceeded the recovery timeout
            const recoveryDuration = Date.now() - this.sttRecoveryStartTime;
            if (recoveryDuration > this.STT_RECOVERY_TIMEOUT_MS) {
                console.warn('⏰ [RecordingManagement] Recovery timeout reached - RESTARTING ENTIRE SESSION');
                if (!component.currentConversationId) {
                    console.error('❌ [RecordingManagement] No conversation ID stored - CRITICAL ERROR');
                    console.error('❌ [RecordingManagement] Component state:', {
                        currentSessionId: component.currentSessionId,
                        currentConversationId: component.currentConversationId,
                        avatar: component.avatar,
                        model: component.model
                    });
                    this.sttRecoveryStartTime = null;
                    this.sttRecoveryAttempts = 0;
                    return;
                }
                try {
                    await this.restartFullSession(component);
                    console.log('✅ [RecordingManagement] Full session restart completed');
                }
                catch (error) {
                    console.error('❌ [RecordingManagement] Full session restart failed:', error);
                }
                this.sttRecoveryStartTime = null;
                this.sttRecoveryAttempts = 0;
                return;
            }
            // Check if we've exceeded maximum restart attempts
            if (this.sttRecoveryAttempts >= this.MAX_STT_RESTART_ATTEMPTS) {
                console.warn(`⚠️ [RecordingManagement] Maximum restart attempts (${this.MAX_STT_RESTART_ATTEMPTS}) reached - stopping restart loop`);
                console.log('🔄 [RecordingManagement] Switching to wait mode - will try full session restart on timeout');
                return;
            }
            // If we're supposed to be recording but got empty transcription, restart STT
            if (component.isRecording && !component.agentSpeaking && !component.isThinking) {
                this.sttRecoveryAttempts++;
                console.log(`🔄 [RecordingManagement] Restarting STT session after empty transcription (attempt ${this.sttRecoveryAttempts}/${this.MAX_STT_RESTART_ATTEMPTS})`);
                try {
                    // Start fresh STT session
                    await component.conversationAgentService.startAudioStreaming(component.currentSessionId, {
                        sampleRate: 16000,
                        channels: 1,
                        bufferSize: 2048,
                        chunkDuration: 100,
                        enableVAD: true
                    });
                    console.log('✅ [RecordingManagement] STT session restarted successfully');
                }
                catch (error) {
                    console.error('❌ [RecordingManagement] Failed to restart STT session:', error);
                }
            }
            return;
        }
        else {
            // We got a valid transcription, reset recovery timer
            if (this.sttRecoveryStartTime !== null) {
                console.log('✅ [RecordingManagement] Valid transcription received, resetting recovery timer');
                this.sttRecoveryStartTime = null;
                this.sttRecoveryAttempts = 0;
            }
        }
        // Clear partial transcription since we now have final
        component.currentTranscription = transcription;
        component.partialTranscription = false;
        // Add transcription to debug data
        component.userTranscriptions.push({
            text: transcription,
            timestamp: new Date()
        });
        // Keep only last 20 transcriptions to prevent memory issues
        if (component.userTranscriptions.length > 20) {
            component.userTranscriptions = component.userTranscriptions.slice(-20);
        }
        // Note: Backend now handles stopping STT session after transcription is sent to LLM
        // and will restart it when needed for agent speech and after agent finishes
        // Note: User message is now added via conversation_message event from backend
        // No need to add it here to avoid duplicates
        // User has finished speaking, agent is thinking
        component.isThinking = true;
    }
    /**
     * Check STT session health and restart if needed
     */
    static async checkSTTSessionHealth(component) {
        // Only check if we should be actively listening
        if (!component.isRecording || component.agentSpeaking || component.isThinking) {
            return;
        }
        // Check if recovery timeout has been exceeded
        if (this.sttRecoveryStartTime && (Date.now() - this.sttRecoveryStartTime) > this.STT_RECOVERY_TIMEOUT_MS) {
            console.warn('⏰ [RecordingManagement] STT recovery timeout reached, stopping health checks');
            this.sttRecoveryStartTime = null;
            this.sttRecoveryAttempts = 0;
            return;
        }
        console.log('🔍 [RecordingManagement] Checking STT session health...');
        // Health check completed - letting natural transcription flow handle recovery
    }
    /**
     * Handle partial transcription (real-time feedback)
     */
    static handlePartialTranscription(component, transcription) {
        console.log('🎙️ Partial transcription received:', transcription);
        // Show partial transcription in real-time
        component.currentTranscription = transcription;
        component.partialTranscription = true;
        // User is actively speaking - this can interrupt agent
        component.userTurn = true;
        component.isThinking = false;
        // If transcription has meaningful content, it's likely a real interruption
        const trimmedText = transcription.trim().toLowerCase();
        if (trimmedText.length > 3 && (component.agentSpeaking || component.isSpeaking)) {
            console.log('🚫 [VoiceChat] User interruption detected via partial transcription:', trimmedText);
            // This interruption logic is handled in the main component's onPartialTranscription callback
        }
    }
    /**
     * Clear transcription state when recording stops or resets
     */
    static clearTranscriptionState(component) {
        component.currentTranscription = '';
        component.partialTranscription = false;
        console.log('🧹 [RecordingManagement] Transcription state cleared');
    }
    /**
     * Add a message to the conversation
     */
    static addMessage(component, role, content) {
        console.log('📝 [RecordingManagement] addMessage called:', {
            role,
            contentLength: content === null || content === void 0 ? void 0 : content.length,
            currentMessagesCount: component.messages.length
        });
        const message = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            role,
            content,
            timestamp: new Date()
        };
        component.messages = [...component.messages, message];
        console.log('emit live chat msg', message);
        component.$emit('live-chat:message-added', { data: { message } });
    }
    /**
     * Check if recording is possible
     */
    static canStartRecording(component) {
        return !!(component.currentSessionId && !component.isRecording);
    }
    /**
     * Get streaming configuration options
     */
    static getStreamingOptions() {
        return {
            sampleRate: 16000,
            channels: 1,
            bufferSize: 2048,
            chunkDuration: 100,
            enableVAD: true
        };
    }
    /**
     * Restart the entire session connection with stored conversation ID
     */
    static async restartFullSession(component) {
        var _a;
        console.log('🔄 [RecordingManagement] RESTARTING ENTIRE SESSION');
        console.log('🔍 [RecordingManagement] Current session:', component.currentSessionId);
        console.log('🔍 [RecordingManagement] Reusing conversation:', component.currentConversationId);
        try {
            // 1. End current session completely
            console.log('🛑 [RecordingManagement] Ending current session...');
            if (component.currentSessionId) {
                await component.conversationAgentService.endConversationAgent(component.currentSessionId, 'stt_recovery');
            }
            // 2. Clear current session state
            component.currentSessionId = null;
            component.isConversationActive = false;
            component.isRecording = false;
            // 3. Start NEW session with the stored conversation ID
            console.log('🚀 [RecordingManagement] Starting fresh session with existing conversation');
            await component.startConversation({
                avatar: component.avatar || '',
                model: component.model,
                hasValidSelection: true,
                voice: ((_a = component.chatSettings) === null || _a === void 0 ? void 0 : _a.voice) || null,
                conversationId: component.currentConversationId // REUSE CONVERSATION
            });
            console.log('✅ [RecordingManagement] Full session restarted successfully');
        }
        catch (error) {
            console.error('❌ [RecordingManagement] Failed to restart full session:', error);
            throw error;
        }
    }
}
exports.RecordingManagementHelper = RecordingManagementHelper;
// Static timeout tracking for continuous recovery
RecordingManagementHelper.sttRecoveryStartTime = null;
RecordingManagementHelper.STT_RECOVERY_TIMEOUT_MS = 120000; // 2 minutes
RecordingManagementHelper.sttRecoveryAttempts = 0;
RecordingManagementHelper.MAX_STT_RESTART_ATTEMPTS = 3; // Maximum restart attempts before giving up
